import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  Image,
  Modal,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';
import Ionicons from '@expo/vector-icons/Ionicons';
import * as ImagePicker from 'expo-image-picker';
import { KeyboardToolbar } from 'react-native-keyboard-controller';

import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { api } from '../../services/api';
import { friendlyApiError } from '../../services/errorMessages';
import { analytics } from '../../services/analytics';
import { BehaviorEvents, trackBehavior } from '../../services/behaviorAnalytics';
import { useModerationNotice } from '../../hooks/useModerationNotice';
import { Typography } from '../ui';
import { AvatarCropModal } from '../profile/AvatarCropModal';
import { missingRequiredHint, requiredFieldError } from './onboardingProfileForm';
import { fontWeights } from '../../styles/typographyTokens';

import { ACCENT_TEXT, ON_ACCENT_TEXT } from '../../styles/accentText';
// Mirrors COVER_PRESETS in DashboardScreen — keep in sync if Dashboard adds
// or removes a preset. Six entries was the agreed set as of 2026-05-08.
const COVER_PRESETS = [
  { id: 'sunrise', gradient: ['#FF9966', '#FF5E62'] as const },
  { id: 'ocean', gradient: ['#2E3192', '#1BFFFF'] as const },
  { id: 'forest', gradient: ['#134E5E', '#71B280'] as const },
  { id: 'night', gradient: ['#141E30', '#243B55'] as const },
  { id: 'mystic', gradient: ['#8E2DE2', '#4A00E0'] as const },
  { id: 'golden', gradient: ['#F2994A', '#F2C94C'] as const },
];

type AvatarFile = Blob | { uri: string; type: string; name: string } | null;

const ERROR_COLOR = '#EF4444';

/**
 * CORE-1906 — уже этого окна в вебе пояснение и кнопка в одну строку не
 * помещаются: кнопка не сжимается, пояснение не бывает уже своего длинного
 * слова, и прижатая вправо строка уезжала за левый край карточки. Тогда
 * пояснение встаёт над кнопкой. Планшеты и альбомная ориентация — шире.
 */
const STACKED_FOOTER_MAX_WIDTH = 480;

/** CORE-298 — строка ошибки под обязательным полем; та же, что у ошибки сохранения. */
function FieldError({ text, testID }: { text: string | null; testID: string }) {
  if (!text) return null;
  return (
    <View testID={testID} style={styles.fieldErrorRow} accessibilityLiveRegion="polite">
      <Ionicons name="alert-circle" size={14} color={ERROR_COLOR} />
      <Text style={{ color: ERROR_COLOR, fontSize: 12 } as any}>{text}</Text>
    </View>
  );
}

/**
 * `compact` (CORE-1309) — анкета открылась поверх Live: только обязательные имя
 * и фамилия. Остальное человек заполнит в профиле, когда эфир закончится.
 */
export function OnboardingProfileModal({ compact = false }: { compact?: boolean } = {}) {
  const { user, refreshUser } = useAuth();
  const { isDark } = useTheme();
  const { width } = useWindowDimensions();
  const stackFooter = Platform.OS === 'web' && width < STACKED_FOOTER_MAX_WIDTH;

  const tc = useMemo(() => ({
    surface: isDark ? '#1D1E22' : '#FFFFFF',
    text: isDark ? '#F5F0EB' : '#2B2730',
    textSec: isDark ? 'rgba(245,240,235,0.7)' : 'rgba(15,20,25,0.55)',
    textMuted: isDark ? 'rgba(245,240,235,0.4)' : 'rgba(15,20,25,0.35)',
    border: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(15,20,25,0.06)',
  }), [isDark]);

  const [editName, setEditName] = useState('');
  const [editSurname, setEditSurname] = useState('');
  const [editIntention, setEditIntention] = useState('');
  const [editBio, setEditBio] = useState('');
  const [editCover, setEditCover] = useState<string>('sunrise');
  const [editAvatarPreview, setEditAvatarPreview] = useState<string | null>(null);
  const [editAvatarFile, setEditAvatarFile] = useState<AvatarFile>(null);
  const [avatarCropUri, setAvatarCropUri] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const moderation = useModerationNotice();

  const requiredHint = missingRequiredHint(editName, editSurname);
  const canSubmit = requiredHint === null;
  // CORE-298 — поле, которое покинули пустым, подсвечивается само: подсказка в
  // футере на телефоне с клавиатурой часто за кадром.
  const [touched, setTouched] = useState<{ name: boolean; surname: boolean }>({ name: false, surname: false });
  const nameError = requiredFieldError('name', editName, touched.name);
  const surnameError = requiredFieldError('surname', editSurname, touched.surname);

  // CORE-497 — onboarding lifecycle: this soft-block modal is the first
  // post-registration step, so its mount == onboarding start / first step view.
  // (This gate can't be dismissed, so there's no skip/close path to emit here.)
  useEffect(() => {
    trackBehavior(BehaviorEvents.onboarding.started, { surface: 'profile_modal' });
    trackBehavior(BehaviorEvents.onboarding.stepViewed, { surface: 'profile_modal', step: 'profile' });
  }, []);

  const handlePickAvatar = useCallback(async () => {
    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ['images'],
      allowsEditing: true,
      aspect: [1, 1],
      quality: 0.85,
    });
    if (result.canceled || !result.assets[0]) return;
    const asset = result.assets[0];
    if (Platform.OS === 'web') {
      // CORE-326 — open the crop modal so the user frames the round avatar.
      setAvatarCropUri(asset.uri);
    } else {
      // Native already cropped via the OS editor (allowsEditing + 1:1).
      setEditAvatarPreview(asset.uri);
      setEditAvatarFile({
        uri: asset.uri,
        type: asset.mimeType || 'image/jpeg',
        name: asset.fileName || 'avatar.jpg',
      });
    }
  }, []);

  const confirmAvatarCrop = useCallback((blob: Blob, previewUri: string) => {
    setEditAvatarFile(blob);
    setEditAvatarPreview(previewUri);
    setAvatarCropUri(null);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!user?.id || !canSubmit) return;
    setIsSaving(true);
    setSaveError('');
    moderation.clear();
    try {
      const fullName = `${editName.trim()} ${editSurname.trim()}`.trim();
      // Pre-publish moderation on the display name — branded banner on block.
      if (!(await moderation.guard(fullName))) {
        setIsSaving(false);
        return;
      }
      await api.updateUserProfile({
        displayName: fullName,
        timezone: user.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
        // CORE-1309 — короткая форма необязательного не показывает и не шлёт:
        // обложку по умолчанию человек не выбирал.
        ...(compact ? {} : {
          avatar: editAvatarFile || undefined,
          bio: editBio.trim() || undefined,
          intention: editIntention.trim() || undefined,
          coverPreset: editCover,
        }),
      });
      analytics.track('onboarding_profile_completed', {
        filledIntention: editIntention.trim() !== '',
        filledBio: editBio.trim() !== '',
        uploadedAvatar: editAvatarFile !== null,
      });
      // CORE-497 — canonical onboarding-completion signal for the interest
      // profile (the legacy `onboarding_profile_completed` above stays for the
      // existing dashboards).
      trackBehavior(BehaviorEvents.onboarding.completed, {
        surface: 'profile_modal',
        filledIntention: editIntention.trim() !== '',
        filledBio: editBio.trim() !== '',
        uploadedAvatar: editAvatarFile !== null,
      });
      // refreshUser() pulls the new displayName; the gate notices the change
      // and unmounts this modal automatically.
      await refreshUser();
    } catch (err: any) {
      // Friendly Russian copy at the UI boundary — maps 413 to «Контент
      // слишком большой», 5xx to «Сервер недоступен», TypeError to «Нет
      // соединения». Raw `err.message` used to leak the technical
      // «Не удалось обработать ответ сервера» whenever the BE responded
      // with a non-JSON body (RU edge / Vercel HTML error pages).
      // Backend 422 moderation rejection → same branded banner.
      if (!moderation.flagFromError(err)) {
        setSaveError(friendlyApiError(err));
      }
    } finally {
      setIsSaving(false);
    }
  }, [
    user, canSubmit, compact, editName, editSurname, editAvatarFile, editBio,
    editIntention, editCover, refreshUser,
  ]);

  return (
    <Modal transparent animationType="fade" visible onRequestClose={() => { /* soft-block: cannot dismiss */ }}>
      <View style={styles.modalBackdrop}>
        <View style={[styles.modalCard, compact && styles.modalCardCompact, { backgroundColor: isDark ? '#1A1A1A' : '#FFFFFF' }]}>
          <View style={styles.modalHeader}>
            <View style={{ flex: 1 }}>
              <Typography variant="h3" style={{ fontSize: 22, fontWeight: fontWeights.bold, color: tc.text } as any}>
                {compact ? 'Как вас зовут' : 'Добро пожаловать в Agapella'}
              </Typography>
              <Text style={{ color: tc.textSec, fontSize: 13, marginTop: 6, lineHeight: 18 } as any}>
                {compact
                  ? 'Так вас увидят ведущий и участники. Фото и рассказ о себе можно добавить позже в профиле.'
                  : 'Расскажите немного о себе — это поможет другим узнавать вас в Кругу.'}
              </Text>
            </View>
          </View>

          <ScrollView
            style={{ maxHeight: 560 }}
            contentContainerStyle={{ paddingBottom: 16 }}
            // Swiping the form down also hides the keyboard (iOS drags it
            // interactively); no-op on web.
            keyboardDismissMode={Platform.OS === 'ios' ? 'interactive' : 'on-drag'}
            keyboardShouldPersistTaps="handled"
          >
            {!compact && (<>
            <Text style={[styles.modalLabel, { color: tc.textSec }]}>ОБЛОЖКА</Text>
            <View style={styles.coverPresets}>
              {COVER_PRESETS.map(c => (
                <TouchableOpacity
                  key={c.id}
                  style={[
                    styles.coverPresetBtn,
                    Platform.OS === 'web' && { backgroundImage: `linear-gradient(135deg, ${c.gradient[0]}, ${c.gradient[1]})` } as any,
                    Platform.OS !== 'web' && { backgroundColor: c.gradient[0] },
                    editCover === c.id && styles.coverPresetActive,
                  ]}
                  onPress={() => setEditCover(c.id)}
                >
                  {editCover === c.id && <Ionicons name="checkmark" size={20} color="#fff" />}
                </TouchableOpacity>
              ))}
            </View>

            <Text style={[styles.modalLabel, { color: tc.textSec }]}>ФОТО ПРОФИЛЯ</Text>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 16, marginBottom: 20 }}>
              <TouchableOpacity testID="onboarding-profile-avatar-button" onPress={handlePickAvatar} style={styles.modalAvatarWrap}>
                {editAvatarPreview ? (
                  <Image source={{ uri: editAvatarPreview }} style={styles.modalAvatar} />
                ) : (
                  <View style={[styles.modalAvatar, { backgroundColor: '#333', alignItems: 'center', justifyContent: 'center' }]}>
                    <Ionicons name="person" size={36} color="rgba(255,255,255,0.4)" />
                  </View>
                )}
                <View style={styles.modalAvatarEdit}>
                  <Ionicons name="camera" size={14} color={ON_ACCENT_TEXT} />
                </View>
              </TouchableOpacity>
              <View style={{ flex: 1 }}>
                <TouchableOpacity onPress={handlePickAvatar}>
                  <Text style={{ color: ACCENT_TEXT, fontWeight: fontWeights.bold, fontSize: 13 } as any}>Загрузить фото</Text>
                </TouchableOpacity>
              </View>
            </View>
            </>)}

            <View style={{ flexDirection: 'row', gap: 12 }}>
              <View style={{ flex: 1 }}>
                <Text style={[styles.modalLabel, { color: tc.textSec }]}>ИМЯ *</Text>
                <TextInput
                  testID="onboarding-profile-name-input"
                  accessibilityLabel="Имя, обязательное поле"
                  style={[styles.modalInput, { borderColor: nameError ? ERROR_COLOR : tc.border, color: tc.text, backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)' }]}
                  value={editName}
                  onChangeText={setEditName}
                  onBlur={() => setTouched((t) => ({ ...t, name: true }))}
                  placeholder="Ваше имя"
                  placeholderTextColor={tc.textMuted}
                  maxLength={50}
                  editable={!isSaving}
                  returnKeyType="done"
                />
                <FieldError testID="onboarding-profile-name-error" text={nameError} />
              </View>
              <View style={{ flex: 1 }}>
                <Text style={[styles.modalLabel, { color: tc.textSec }]}>ФАМИЛИЯ *</Text>
                <TextInput
                  testID="onboarding-profile-surname-input"
                  accessibilityLabel="Фамилия, обязательное поле"
                  style={[styles.modalInput, { borderColor: surnameError ? ERROR_COLOR : tc.border, color: tc.text, backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)' }]}
                  value={editSurname}
                  onChangeText={setEditSurname}
                  onBlur={() => setTouched((t) => ({ ...t, surname: true }))}
                  placeholder="Ваша фамилия"
                  placeholderTextColor={tc.textMuted}
                  maxLength={50}
                  editable={!isSaving}
                  returnKeyType="done"
                />
                <FieldError testID="onboarding-profile-surname-error" text={surnameError} />
              </View>
            </View>

            {!compact && (<>
            <Text style={[styles.modalLabel, { color: tc.textSec }]}>НАМЕРЕНИЕ</Text>
            <TextInput
              style={[styles.modalInput, { borderColor: tc.border, color: tc.text, backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)', minHeight: 72, textAlignVertical: 'top' } as any]}
              value={editIntention}
              onChangeText={setEditIntention}
              placeholder="Например: «Хочу медитировать ежедневно 10 минут ближайшие 4 месяца»"
              placeholderTextColor={tc.textMuted}
              multiline
              numberOfLines={3}
              maxLength={140}
              editable={!isSaving}
            />

            <Text style={[styles.modalLabel, { color: tc.textSec }]}>О СЕБЕ</Text>
            <TextInput
              style={[styles.modalInput, { borderColor: tc.border, color: tc.text, backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)', minHeight: 100, textAlignVertical: 'top' } as any]}
              value={editBio}
              onChangeText={setEditBio}
              placeholder="Пара слов о себе. Откуда вы, чем живёте, что для вас важно."
              placeholderTextColor={tc.textMuted}
              multiline
              numberOfLines={5}
              // CORE-94: «обо мне» больше не ограничено 500 знаками — выровнено
              // с редактором профиля мастера в Settings (maxLength 8000).
              maxLength={8000}
              editable={!isSaving}
            />
            </>)}

            {moderation.banner}

            {saveError ? (
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 12 }}>
                <Ionicons name="alert-circle" size={14} color="#EF4444" />
                <Text style={{ color: '#EF4444', fontSize: 13 } as any}>{saveError}</Text>
              </View>
            ) : null}
          </ScrollView>

          <View style={[styles.modalFooter, stackFooter && styles.modalFooterStacked]}>
            {/* CORE-298 — серая кнопка без объяснения читалась как поломка. */}
            <Text testID="onboarding-profile-required-hint" style={{ flex: stackFooter ? undefined : 1, color: tc.textSec, fontSize: 12, lineHeight: 16 } as any}>
              {requiredHint ?? (compact ? '' : '* — обязательные поля, остальное по желанию')}
            </Text>
            <TouchableOpacity
              testID="onboarding-profile-submit"
              style={[styles.modalBtn, styles.modalBtnPrimary, (!canSubmit || isSaving) && { opacity: 0.5 }]}
              onPress={handleSubmit}
              disabled={!canSubmit || isSaving}
            >
              {isSaving ? (
                <ActivityIndicator color={ON_ACCENT_TEXT} size="small" />
              ) : (
                <Text style={{ color: ON_ACCENT_TEXT, fontWeight: fontWeights.bold, fontSize: 14 } as any}>Сохранить и продолжить</Text>
              )}
            </TouchableOpacity>
          </View>
        </View>
      </View>

      {/* «Готово» bar above the keyboard (tester: no way to hide it on
          iPhone/Android — multiline fields swallow Return). iOS + Android;
          web keyboards dismiss themselves. */}
      {Platform.OS !== 'web' && (
        <KeyboardToolbar doneText="Готово" showArrows={false} />
      )}

      <AvatarCropModal
        imageUri={avatarCropUri}
        onCancel={() => setAvatarCropUri(null)}
        onConfirm={confirmAvatarCrop}
      />
    </Modal>
  );
}

const styles = StyleSheet.create({
  modalBackdrop: {
    flex: 1, backgroundColor: 'rgba(0,0,0,0.5)',
    alignItems: 'center', justifyContent: 'center',
    ...(Platform.OS === 'web' && { backdropFilter: 'blur(4px)' }),
    paddingHorizontal: 16,
  } as any,
  modalCard: {
    width: '100%', maxWidth: 760, borderRadius: 24,
    padding: 32, maxHeight: '92%',
    ...(Platform.OS === 'web' && { boxShadow: '0 24px 80px rgba(0,0,0,0.2)' }),
  } as any,
  modalCardCompact: { maxWidth: 440, padding: 24 },
  modalHeader: {
    flexDirection: 'row', justifyContent: 'space-between',
    alignItems: 'center', marginBottom: 24,
  },
  modalLabel: {
    fontSize: 11, fontWeight: fontWeights.bold, letterSpacing: 1.5,
    textTransform: 'uppercase', marginBottom: 10, marginTop: 4,
  } as any,
  modalInput: {
    borderWidth: 1, borderRadius: 12,
    paddingHorizontal: 14, paddingVertical: 12,
    fontSize: 14, marginBottom: 16,
    ...(Platform.OS === 'web' && { outlineStyle: 'none' }),
  } as any,
  // Поле оставляет под собой marginBottom 16; строка ошибки встаёт в этот зазор.
  fieldErrorRow: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
    marginTop: -10, marginBottom: 14,
  },
  modalFooter: {
    flexDirection: 'row', justifyContent: 'flex-end', alignItems: 'center', gap: 10,
    marginTop: 20, paddingTop: 20, borderTopWidth: 1, borderTopColor: 'rgba(0,0,0,0.06)',
  },
  // CORE-1906 — телефон в вебе: пояснение над кнопкой, кнопка во всю ширину.
  modalFooterStacked: { flexDirection: 'column', alignItems: 'stretch' },
  modalBtn: {
    paddingHorizontal: 20, paddingVertical: 11,
    borderRadius: 10, borderWidth: 1, borderColor: 'rgba(0,0,0,0.1)',
    alignItems: 'center', justifyContent: 'center',
    ...(Platform.OS === 'web' && { cursor: 'pointer' }),
  } as any,
  modalBtnPrimary: { backgroundColor: '#E98245', borderColor: '#E98245' },
  coverPresets: {
    flexDirection: 'row', flexWrap: 'wrap', gap: 10, marginBottom: 20,
  },
  coverPresetBtn: {
    width: 72, height: 48, borderRadius: 10,
    alignItems: 'center', justifyContent: 'center',
    borderWidth: 2, borderColor: 'transparent',
    ...(Platform.OS === 'web' && { cursor: 'pointer' }),
  } as any,
  coverPresetActive: { borderColor: '#E98245' } as any,
  modalAvatarWrap: {
    position: 'relative',
    ...(Platform.OS === 'web' && { cursor: 'pointer' }),
  } as any,
  modalAvatar: { width: 72, height: 72, borderRadius: 36 } as any,
  modalAvatarEdit: {
    position: 'absolute', bottom: -2, right: -2,
    width: 26, height: 26, borderRadius: 13,
    backgroundColor: '#E98245',
    alignItems: 'center', justifyContent: 'center',
    borderWidth: 2, borderColor: '#fff',
  },
});
